import { BlogPost } from "./types";

export const optimizarImagenesParaWeb: BlogPost = {
  slug: "optimizar-imagenes-para-web",
  title: "Optimizar imágenes para web: guía técnica con métricas reales",
  description: "Guía completa de optimización de imágenes para sitios web. Formatos, dimensiones, lazy loading, Core Web Vitals, srcset, y cómo medir el impacto en la velocidad de carga.",
  date: "2026-03-24",
  readTime: "11 min",
  keywords: "optimizar imagenes para web, imágenes web rendimiento, comprimir imagenes sitio web, webp imagenes web, core web vitals imagenes",
  content: `
## Por qué las imágenes son el factor más crítico en la velocidad web

Las imágenes son el tipo de recurso más pesado en la mayoría de páginas web. Según el HTTP Archive (datos de 2024):

- Las imágenes representan el **42% del peso total** de las páginas web
- Una página web media transfiere **1.8MB en imágenes** por visita
- Las imágenes no optimizadas son la causa principal de puntuaciones bajas en Lighthouse

La relación directa con el negocio: Google usa Core Web Vitals (métricas de velocidad) como factor de ranking desde 2021. Una página lenta no solo frustra a los usuarios — también pierde posiciones en los resultados de búsqueda.

## Core Web Vitals relacionados con imágenes

Google mide tres métricas principales de experiencia de usuario:

### LCP (Largest Contentful Paint)

Tiempo hasta que el elemento visual más grande de la página está cargado. **Las imágenes son frecuentemente el elemento LCP.**

- Objetivo: LCP < 2.5 segundos
- Problemático: LCP > 4 segundos

**Cómo las imágenes afectan el LCP:** una imagen hero de 800KB puede retrasar el LCP en 1-2 segundos en conexiones móviles. La misma imagen optimizada a 80KB carga en 100-200ms.

### CLS (Cumulative Layout Shift)

Inestabilidad visual causada por elementos que se mueven mientras la página carga. Las imágenes sin dimensiones definidas causan CLS porque el navegador no reserva espacio hasta que se carga la imagen.

**Solución:** siempre define \`width\` y \`height\` en las etiquetas \`<img>\`:
\`\`\`html
<img src="foto.jpg" width="800" height="600" alt="descripción">
\`\`\`

### FID/INP (Interaction to Next Paint)

Velocidad de respuesta a interacciones del usuario. Las imágenes pesadas afectan indirectamente al INP porque saturan el ancho de banda disponible.

## Formatos de imagen para web: guía actualizada 2024-2026

### WebP: el estándar recomendado

WebP es el formato recomendado para web en 2024. Ventajas frente a JPEG:
- 25-35% más pequeño con la misma calidad visual
- Soporta transparencia (como PNG) con compresión mucho mejor
- Soporta animación (alternativa a GIF)
- Compatible con el 97%+ de navegadores modernos

**Cuándo usar WebP:** prácticamente siempre en producción web, excepto cuando necesitas compatibilidad con sistemas que no soportan WebP (algunos procesadores de email, aplicaciones legacy).

### AVIF: el futuro (con cautela)

AVIF es un formato más reciente que supera a WebP en compresión:
- 40-50% más pequeño que JPEG con calidad equivalente
- Soporta alto rango dinámico (HDR)
- Compatibilidad (2024): ~90% de navegadores

La limitación: la codificación AVIF es significativamente más lenta. Para CDNs con recursos limitados, WebP sigue siendo más práctico.

### JPEG: sigue siendo válido

JPEG sigue siendo perfectamente válido cuando:
- La audiencia incluye sistemas que no soportan WebP
- El flujo de trabajo ya está establecido en JPEG
- La diferencia de tamaño no justifica el cambio de tooling

### PNG: solo cuando hay transparencia

PNG sigue siendo necesario para:
- Logos con transparencia sin alternativa WebP
- Capturas de pantalla con texto fino
- Imágenes con gradientes artificiales donde los artefactos JPEG son inaceptables

Evita PNG para fotografías — son entre 5-15 veces más grandes que JPEG equivalente.

### SVG: para gráficos vectoriales

Para logos, iconos e ilustraciones que se pueden representar como vectores, SVG es infinitamente superior a cualquier formato rasterizado:
- Escala perfecta a cualquier tamaño
- Generalmente entre 1-20KB
- Se puede animar con CSS

## Dimensiones correctas según el contexto

Una de las optimizaciones más impactantes y más ignoradas: no servir imágenes más grandes de lo que se van a mostrar.

| Posición en la web | Ancho máximo recomendado | Peso objetivo |
|---|---|---|
| Imagen hero o banner full-width | 1600px | 80-150KB (WebP) / 150-250KB (JPEG) |
| Imagen de artículo de blog | 800px | 40-80KB (WebP) / 80-150KB (JPEG) |
| Imagen de tarjeta o thumbnail | 400px | 20-40KB (WebP) / 40-70KB (JPEG) |
| Foto de perfil o avatar | 200px | 10-20KB |
| Logo | Según diseño | SVG o PNG-8 < 20KB |
| Imagen en sidebar | 300px | 15-30KB (WebP) |

Servir una imagen de 2400px en un contenedor de 400px es desperdiciar 6x el ancho de banda innecesariamente.

## Responsive images: srcset y sizes

Para servir el tamaño correcto según el dispositivo, usa el atributo \`srcset\`:

\`\`\`html
<img
  src="foto-800.jpg"
  srcset="foto-400.jpg 400w,
          foto-800.jpg 800w,
          foto-1200.jpg 1200w,
          foto-1600.jpg 1600w"
  sizes="(max-width: 600px) 100vw,
         (max-width: 1200px) 50vw,
         800px"
  alt="descripción de la imagen"
  width="800"
  height="600"
  loading="lazy"
>
\`\`\`

Esto hace que dispositivos móviles de pantalla pequeña descarguen la imagen de 400px y escritorios de alta resolución descarguen la de 1600px. La reducción de ancho de banda puede ser del 60-70% en móviles.

## Lazy loading: no cargues lo que no se ve

El atributo \`loading="lazy"\` le dice al navegador que no cargue la imagen hasta que esté cerca de entrar en el viewport:

\`\`\`html
<img src="foto.jpg" loading="lazy" alt="descripción">
\`\`\`

**Importante:** no uses \`loading="lazy"\` en la imagen LCP (la primera imagen visible de la página). Para esa imagen, usa \`fetchpriority="high"\`:

\`\`\`html
<!-- Imagen LCP - carga prioritaria -->
<img src="hero.jpg" fetchpriority="high" alt="descripción del hero">

<!-- Resto de imágenes - carga diferida -->
<img src="foto2.jpg" loading="lazy" alt="descripción">
\`\`\`

## Preload para la imagen LCP

Para mejorar aún más el LCP, indica al navegador que pre-cargue la imagen principal antes de que el HTML la encuentre:

\`\`\`html
<head>
  <link rel="preload" as="image" href="hero.jpg" fetchpriority="high">
</head>
\`\`\`

Esta técnica puede mejorar el LCP entre 200-500ms en conexiones lentas.

## Formatos modernos automáticos con CDN

Si usas un CDN, configúralo para servir WebP automáticamente:

**Cloudflare:** activa "Polish" para compresión automática y conversión WebP.

**Next.js:** el componente \`<Image>\` genera WebP/AVIF automáticamente y maneja srcset:
\`\`\`jsx
import Image from 'next/image'
<Image src="/foto.jpg" width={800} height={600} alt="descripción" />
\`\`\`

**Cloudinary/Imgix:** añade \`f_auto,q_auto\` a las URLs para conversión y compresión automática.

## Proceso de optimización manual: paso a paso

Para sitios sin CDN de imágenes:

1. **Inventario**: identifica las imágenes más pesadas con Lighthouse (Network tab, filtrar por "img")
2. **Redimensiona** cada imagen a su tamaño máximo de visualización
3. **Convierte a WebP** usando nuestro [compresor de imágenes](/)
4. **Comprime** al 80-85% de calidad
5. **Actualiza el HTML** para referenciar los nuevos archivos optimizados
6. **Añade srcset** si sirves la misma imagen a distintos tamaños de pantalla
7. **Verifica** con Lighthouse antes y después para confirmar la mejora

## Herramientas de medición y auditoría

### Google PageSpeed Insights / Lighthouse

La herramienta más usada. Da recomendaciones específicas incluyendo:
- "Serve images in next-gen formats" (WebP/AVIF)
- "Properly size images" (imágenes sobredimensionadas)
- "Defer offscreen images" (lazy loading)
- "Efficiently encode images" (compresión insuficiente)

### Chrome DevTools

Network tab → filtrar por Img → ver cada imagen con su tamaño real. Compara el tamaño de la imagen con sus dimensiones visuales en la página.

## Preguntas frecuentes

### ¿Debo convertir todas mis imágenes a WebP ahora mismo?

Si tu sitio ya está en producción, la conversión a WebP es una mejora recomendada pero no urgente. Prioriza si tienes imágenes JPEG pesadas (>200KB) en posiciones críticas de la página, especialmente la imagen LCP. Para sitios nuevos, usa WebP desde el principio.

### ¿Qué pasa si Google indexa mis imágenes en PNG y las cambio a WebP?

Google indexa la URL de la imagen, no el formato. Si cambias el archivo y actualizas el HTML, Google re-indexará las nuevas URLs. No hay penalización por el cambio de formato, y a Google le gustan las páginas más rápidas.

### ¿Cuánto mejora el LCP con imágenes optimizadas?

En páginas con imágenes hero de 500KB-1MB, optimizarlas a 80-120KB puede reducir el LCP en 1-3 segundos en conexiones móviles típicas (4G promedio de ~10Mbps). En 3G (2-3Mbps), la mejora puede ser de 3-5 segundos.

### ¿Necesito imágenes en 2x para pantallas Retina?

Para pantallas con ratio de pixels 2x (Retina, pantallas 4K), sirves una imagen el doble de grande para que se vea nítida. Con srcset esto es automático: \`foto-400.jpg 400w, foto-800.jpg 800w\` sirve la de 800px en pantallas Retina de 400px CSS. El coste es mayor ancho de banda, pero la experiencia visual es significativamente mejor en dispositivos modernos.

### ¿Las imágenes optimizadas afectan al SEO de imágenes?

Las imágenes optimizadas mejoran el SEO general (velocidad de página). El texto alternativo (\`alt\`) y el contexto de la página siguen siendo el factor más importante para el SEO de imágenes específicamente. La velocidad de carga es un factor de ranking general pero no específico de Image Search.

## Conclusión

La optimización de imágenes para web tiene tres pilares: formato correcto (WebP donde sea posible), dimensiones adecuadas (no más grande de lo que se muestra), y carga diferida (lazy loading para imágenes fuera del viewport).

Implementando solo estos tres aspectos, la mayoría de sitios pueden reducir el peso total de imágenes en un 60-75%, con impacto directo en Core Web Vitals y experiencia de usuario.

Para optimizar tus imágenes antes de subirlas, usa nuestro [compresor de imágenes](/) que convierte a WebP y ajusta la calidad para el peso objetivo que necesitas.
  `
};
